import classNames from 'classnames';

import lang from '../../../../lib/language';
import { isEmpty } from '../../../../lib/util/typeGuards';

import CompanyLogo from '../../../../components/CompanyLogo/CompanyLogo';
import ClickableDiv from '../../../ClickableDiv/ClickableDiv';
import useIsMobile from '../../../hooks/useIsMobile';
import NoDataAvailable from '../../../NoDataAvailable/NoDataAvailable';
import { EPIC_MOVERS_ROW_COUNT, EPIC_MOVERS_TABLE_HEADERS } from '../../EpicMovers.constants';
import { EpicMoversListProps, EpicMoversRow } from '../../EpicMovers.types';
import PriceAndChange from '../PriceAndChange/PriceAndChange';

import EpicMoversLoaderRow from './EpicMoversLoaderRow';

import styles from '../../EpicMovers.module.scss';

const MINIMUM_RESULTS = 4;
const LOADER_ROW_COUNT = 4;
const LOADING_NAME_WIDTH = 134;

const loaderRows = Array.from({ length: LOADER_ROW_COUNT }).map((_, index) => (
  // eslint-disable-next-line react/no-array-index-key
  <EpicMoversLoaderRow key={`epic-movers-loader-${index}`} />
));

const EpicMoversList = ({
  data,
  isReduction,
  title,
  isPending,
  isReady,
  onSymbolClick,
}: EpicMoversListProps) => {
  const { isMobile } = useIsMobile();
  const tableHeaders1 = [title].map((item: string) => <div className={styles.tableHeadCell} key={item}>{item}</div>);
  const tableHeaders2 = EPIC_MOVERS_TABLE_HEADERS.map((item: string) => <div key={item}>{item}</div>);
  const hasNoAnyData = isEmpty(data) && !isPending;
  const hasRows = !isEmpty(data);

  const renderList = () => {
    if (hasNoAnyData) {
      return (
        <NoDataAvailable
          className={styles.noDataToDisplayInside}
          isNoDataIcon
          title={lang.commonDiscover52WeekNoData()}
        />
      );
    }
    const results = data.slice(0, EPIC_MOVERS_ROW_COUNT).map((item: EpicMoversRow) => {
      const { logo, url, displayName } = item;

      if (isMobile) {
        return (
          <ClickableDiv
            key={item.symbol}
            onClick={() => onSymbolClick(item.symbol)}
            className={styles.wrapperCompanyMob}
          >
            <div className={styles.left}>
              <div className={styles.logoWrapper}>
                {(url || logo) && !isPending ? (
                  <CompanyLogo url={url || ''} logo={logo || ''} symbol={item.symbol} />
                ) : (
                  <div className={styles.loadingLogo} />
                )}
              </div>
              <div className={styles.companyInfo}>
                <p className={GlobalClassesEnum.CommonBody1TextBold}>{item.symbol}</p>
                <p className={styles.symbolDisplayName}>
                  {displayName}
                </p>
              </div>
            </div>
            <div className={styles.right}>
              <PriceAndChange item={item} isReduction={isReduction} isLoading={isPending} />
            </div>
          </ClickableDiv>
        );
      }

      return (
        <ClickableDiv
          className={styles.tableRow}
          key={item.symbol}
          onClick={() => onSymbolClick(item.symbol)}
        >
          <div className={styles.wrapperCompany}>
            {(url || logo) && isReady ? (
              <CompanyLogo url={url || ''} logo={logo || ''} symbol={item.symbol} />
            ) : (
              <div className={styles.loadingLogo} />
            )}
            <div className={styles.companyInfo}>
              {!item.symbol || !displayName ? (
                <>
                  <div className={classNames(styles.loadingComponent, styles.loadingComponentMargin)} />
                  <div className={styles.loadingComponent} style={{ width: LOADING_NAME_WIDTH }} />
                </>
              ) : (
                <>
                  <p className={GlobalClassesEnum.CommonBody1TextBold}>{item.symbol}</p>
                  <p className={styles.symbolDisplayName}>{displayName}</p>
                </>
              )}
            </div>
          </div>
          <div className={styles.priceChange}>
            <PriceAndChange item={item} isReduction={isReduction} isLoading={isPending} />
          </div>
        </ClickableDiv>
      );
    });

    while (results.length < MINIMUM_RESULTS && !hasNoAnyData) {
      results.push(
        <div className={styles.emptyCompanyField} key={`empty-${results.length}`} />,
      );
    }

    return results;
  };

  if (isMobile) {
    return (
      <div className={styles.tableMobile}>
        {!hasNoAnyData && (
          <div className={styles.tableHeaders}>
            <div>{EPIC_MOVERS_TABLE_HEADERS[0]}</div>
            <div>{EPIC_MOVERS_TABLE_HEADERS[1]}</div>
          </div>
        )}
        {isPending && !hasRows ? loaderRows : renderList()}
      </div>
    );
  }

  return (
    <div className={styles.table}>
      <div className={styles.theadWrapper}>
        <div className={styles.tr}>{tableHeaders1}</div>
        <div className={classNames(styles.tr, { [styles.invisibleTr]: hasNoAnyData })}>{tableHeaders2}</div>
      </div>
      <div className={styles.tableBody}>
        {isPending && !hasRows ? loaderRows : renderList()}
      </div>
    </div>
  );
};

export default EpicMoversList;
